/**
 * Alerts Section Component
 * Container for dashboard alerts with collapsible behaviour
 * Automatically collapses when no alerts exist
 */

import { useTranslation } from 'react-i18next'
import { Bell } from 'lucide-react'
import { CollapsibleSection } from './CollapsibleSection'
import { AlertCard } from './AlertCard'
import type { DashboardAlert } from '../../types/dashboard'

interface AlertsSectionProps {
  alerts: DashboardAlert[]
  loading?: boolean
}

export function AlertsSection({
  alerts,
  loading = false,
}: AlertsSectionProps): React.ReactElement | null {
  const { t } = useTranslation('admin')

  // Don't render if no alerts and not loading
  if (!loading && alerts.length === 0) {
    return null
  }

  const badge = alerts.length > 0 && (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
      <Bell className="h-3 w-3" />
      {alerts.length}
    </span>
  )

  return (
    <CollapsibleSection
      id="alerts"
      title={t('dashboard.attentionRequired')}
      badge={badge}
      defaultOpen={alerts.length > 0}
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="db-spinner h-6 w-6 animate-spin rounded-full border-2" />
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <AlertCard key={alert.id} alert={alert} />
          ))}
        </div>
      )}
    </CollapsibleSection>
  )
}
